import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@glplus/glpweb-dsl';
import { guideByPath } from '../kit/content';
import { irsLink } from '../kit/irsLinks';
import { KitDisclaimer, KitDisclaimerLine } from '../kit/Disclaimer';
import { RECOMMENDED, RECOMMENDED_CATEGORIES } from '../kit/recommended';
import { downloadCsv, MILEAGE_LOG, EXPENSE_TRACKER } from '../kit/tools/csv';
import { Button, Chip } from '@glplus/glpweb-dsl';
import type { Lang } from '../kit/types';

export default function KitGuide() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language.startsWith('es') ? 'es' : 'en') as Lang;
  const { category = '', guide = '' } = useParams();
  const hit = guideByPath(category, guide);
  if (!hit) return <Alert severity="warning">{t('kit.notFound')}</Alert>;
  const { category: c, guide: g } = hit;
  const b = g.body[lang];
  const links = (g.irsLinks ?? []).map(irsLink).filter((l): l is NonNullable<typeof l> => Boolean(l));
  const related = (g.related ?? []).map((r) => { const [cs, gs] = r.split('/'); return guideByPath(cs, gs); }).filter((x): x is NonNullable<typeof x> => Boolean(x));

  return (
    <Box data-testid="page-kit-guide" sx={{ maxWidth: 820 }}>
      <Typography component={Link} to={`/kit/${c.slug}`} sx={{ color: '#1f7a3d', fontWeight: 600, fontSize: 13, textDecoration: 'none', display: 'inline-block', mb: 1.5 }}>{t('kit.backCategory', { category: c.label[lang] })}</Typography>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' }, mb: 0.5 }}>{b.title}</Typography>
      <Typography sx={{ color: '#4b5563', mb: 2.5, fontSize: '16px' }}>{b.summary}</Typography>
      {c.half === 'business' ? <KitDisclaimer /> : <KitDisclaimerLine />}
      <Box sx={{ backgroundColor: '#fff', borderRadius: 2.5, borderTop: `3px solid ${c.accent}`, p: { xs: 2, md: 3.5 }, boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
        {b.sections.map((s, i) => (
          <Box key={i} sx={{ mb: 2.5 }}>
            {s.heading && <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937', mb: 1 }}>{s.heading}</Typography>}
            {s.paragraphs?.map((p, j) => <Typography key={j} sx={{ color: '#374151', fontSize: '15px', lineHeight: 1.65, mb: 1 }}>{p}</Typography>)}
            {s.bullets && (
              <Box component="ul" sx={{ pl: 2.5, m: 0, color: '#374151', fontSize: '15px', lineHeight: 1.65 }}>
                {s.bullets.map((li, j) => <li key={j}>{li}</li>)}
              </Box>
            )}
          </Box>
        ))}
        {b.actions && b.actions.length > 0 && (
          <Box sx={{ mt: 1, p: 2, borderRadius: 2, backgroundColor: '#ecfdf3' }}>
            <Typography sx={{ fontWeight: 800, color: '#15803d', fontSize: '13px', letterSpacing: 0.8, mb: 0.5 }}>{t('kit.actions').toUpperCase()}</Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, color: '#1f2937', fontSize: '15px', lineHeight: 1.65 }}>
              {b.actions.map((a, j) => <li key={j}>{a}</li>)}
            </Box>
          </Box>
        )}
      </Box>
      {RECOMMENDED_CATEGORIES.has(c.slug) && <RecommendedBlock lang={lang} />}
      {links.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '14px', mb: 1 }}>{t('kit.officialForms')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {links.map((l) => (
              <Typography key={l.key} component="a" className="runner-button-link" href={l.url} target="_blank" rel="noopener noreferrer" sx={{ px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #e2e8f0', color: '#343F5B', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}>{l.form} · {l.label[lang]} ↗</Typography>
            ))}
          </Box>
        </Box>
      )}
      {related.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '14px', mb: 1 }}>{t('kit.related')}</Typography>
          {related.map(({ category: rc, guide: rg }) => (
            <Typography key={`${rc.slug}/${rg.slug}`} component={Link} to={`/kit/${rc.slug}/${rg.slug}`} sx={{ display: 'block', color: '#343F5B', fontWeight: 600, fontSize: '14px', textDecoration: 'none', py: 0.5 }}>→ {rg.body[lang].title} <Typography component="span" sx={{ color: '#5f6b7a', fontSize: '12px' }}>· {rc.label[lang]}</Typography></Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Ours first (free, no sign-up), then a paid app and a free app side by side
 * with the honest note on each (Rod 2026-09-10: "show free option as well and
 * just be honest"). Only on the two money-tracking categories.
 */
function RecommendedBlock({ lang }: { lang: Lang }) {
  const { t } = useTranslation();
  return (
    <>
      <Box sx={{ mt: 2.5, backgroundColor: '#fff', borderRadius: 2.5, borderTop: '3px solid #2E9E3A', p: { xs: 2, md: 3 }, boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937', mb: 1.5 }}>{t('kit.recommended.oursTitle')}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button ariaLabel={`${t('kit.tools.download')} — ${t('kit.tools.mileage.title')}`} buttonText={`${t('kit.tools.mileage.title')} · ${t('kit.tools.download')}`} variant="outlined" size="small" onClick={() => downloadCsv(lang === 'es' ? 'registro-de-millas.csv' : 'mileage-log.csv', MILEAGE_LOG[lang])} sx={{ backgroundColor: '#fff', borderColor: '#d0d5dd', color: '#1f2937', fontWeight: 700 }} />
          <Button ariaLabel={`${t('kit.tools.download')} — ${t('kit.tools.expense.title')}`} buttonText={`${t('kit.tools.expense.title')} · ${t('kit.tools.download')}`} variant="outlined" size="small" onClick={() => downloadCsv(lang === 'es' ? 'registro-de-gastos.csv' : 'expense-tracker.csv', EXPENSE_TRACKER[lang])} sx={{ backgroundColor: '#fff', borderColor: '#d0d5dd', color: '#1f2937', fontWeight: 700 }} />
        </Box>
      </Box>
      <Box sx={{ mt: 2.5, backgroundColor: '#fff', borderRadius: 2.5, borderTop: '3px solid #343F5B', p: { xs: 2, md: 3 }, boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937' }}>{t('kit.recommended.title')}</Typography>
        <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 0.5, mb: 2 }}>{t('kit.recommended.intro')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {RECOMMENDED.map((r) => (
            <Box key={r.key} sx={{ p: 2, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, color: '#1f2937', fontSize: '15px' }}>{r.name}</Typography>
                <Chip label={r.paid ? t('kit.recommended.paid') : t('kit.recommended.free')} size="small" backgroundColor={r.paid ? '#e8ebf2' : '#dcfce7'} textColor={r.paid ? '#343F5B' : '#15803d'} fontWeight={700} fontSize={11} />
              </Box>
              <Typography sx={{ color: '#374151', fontSize: '14px', flex: 1 }}>{r.blurb[lang]}</Typography>
              <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{r.paid ? t('kit.recommended.affiliateNote') : t('kit.recommended.noAffiliateNote')}</Typography>
              <Typography component="a" className="runner-button-link" href={r.url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`kit-rec-${r.key}`} sx={{ alignSelf: 'flex-start', px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #d0d5dd', color: '#1f2937', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}>
                {r.paid ? t('kit.recommended.seePlans') : t('kit.recommended.getApp')}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}>{t('kit.recommended.footer')}</Typography>
      </Box>
    </>
  );
}
